import React, { useState, useEffect } from 'react';
import { Container, Card, Table, Button, Spinner, Alert, Modal, Badge } from 'react-bootstrap';
import { getSavedTourPlans, downloadTourPlanById, deleteTourPlan } from '../../../service/workflow-api';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faPrint, faEye, faTrash, faArrowLeft, faDownload } from '@fortawesome/free-solid-svg-icons';
import { useHistory } from 'react-router-dom';
import '../common.css';

// CSS for pale blue buttons
const paleBlueButtonStyle = {
  backgroundColor: '#add8e6',
  borderColor: '#add8e6',
  color: '#1C2B4D'
};

const paleBlueButtonHoverStyle = {
  backgroundColor: '#9BC4D4',
  borderColor: '#9BC4D4',
  color: '#1C2B4D'
};

// CSS for blue buttons
const blueButtonStyle = {
  backgroundColor: 'rgb(77, 122, 255)',
  borderColor: 'rgb(77, 122, 255)',
  color: '#fff'
};

const blueButtonHoverStyle = {
  backgroundColor: 'rgb(60, 105, 240)',
  borderColor: 'rgb(60, 105, 240)',
  color: '#fff'
};

const SavedTourPlans = () => {
  const [tourPlans, setTourPlans] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [selectedPlan, setSelectedPlan] = useState(null);
  const [viewModalOpen, setViewModalOpen] = useState(false);
  const [deleteModalOpen, setDeleteModalOpen] = useState(false);
  const [planToDelete, setPlanToDelete] = useState(null);
  const [loadingPlanContent, setLoadingPlanContent] = useState(false);
  const [htmlContent, setHtmlContent] = useState('');
  const history = useHistory();

  // Fetch saved tour plans on component mount
  useEffect(() => {
    fetchTourPlans();
  }, []);

  // Function to fetch tour plans
  const fetchTourPlans = async () => {
    setLoading(true);
    setError(null);
    try {
      const data = await getSavedTourPlans();
      setTourPlans(data || []);
    } catch (err) {
      console.error('Error fetching tour plans:', err);
      setError('Failed to load saved tour plans. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  // Format date for display
  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  // Handle viewing a tour plan
  const handleViewPlan = async (plan) => {
    setSelectedPlan(plan);
    setLoadingPlanContent(true);
    setViewModalOpen(true);
    
    try {
      const response = await downloadTourPlanById(plan.id);
      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }
      const data = await response.json();
      setHtmlContent(data.html); // Now expecting HTML instead of markdown
    } catch (err) {
      console.error('Error fetching tour plan content:', err);
      setError('Failed to load tour plan content. Please try again.');
    } finally {
      setLoadingPlanContent(false);
    }
  };

  // Handle showing the delete confirmation modal
  const handleShowDeleteModal = (plan) => {
    setPlanToDelete(plan);
    setDeleteModalOpen(true);
  };

  // Handle confirming deletion of a tour plan
  const handleConfirmDelete = async () => {
    if (!planToDelete) return;
    
    try {
      await deleteTourPlan(planToDelete.id);
      // Remove the deleted plan from the state
      setTourPlans(tourPlans.filter(plan => plan.id !== planToDelete.id));
      setDeleteModalOpen(false);
      setPlanToDelete(null);
    } catch (err) {
      console.error('Error deleting tour plan:', err);
      setError('Failed to delete tour plan. Please try again.');
    }
  };

  // Handle printing the tour plan
  const handlePrintPlan = () => {
    if (!htmlContent) { // htmlContent is from downloadTourPlanById
      alert('No content available to print.');
      return;
    }
    const printWindow = window.open('', '_blank');
    if (!printWindow) {
      alert('Please allow pop-ups to print the tour plan.');
      return;
    }
    printWindow.document.write(htmlContent); // Assumes htmlContent from backend is fully styled for screen and print
    printWindow.document.close();
    // Timeout to allow content to render, especially if there are images or complex CSS
    printWindow.setTimeout(() => {
      printWindow.focus(); // Focus the new window before printing
      try {
        printWindow.print();
      } catch (e) {
        console.error("Print error:", e);
        alert("An error occurred while trying to print. Please try again.");
      }
      // Optional: close the window after printing, but often better to leave it for the user.
      // if (printWindow.close) {
      //   printWindow.close();
      // }
    }, 1000); // 1 second delay, adjust if needed
  };

  return (
    <Container fluid className="p-0 dark-container">
      <div className="dark-header py-2 glass-bg mb-4" style={{ borderRadius: '0 0 12px 12px' }}>
        <Container>
          <div className="d-flex justify-content-between align-items-center">
            <h2 className="mb-0 h5" style={{ color: 'rgb(77, 122, 255)' }}>Saved Tour Plans</h2>
            <Button 
              variant="primary"
              size="sm" 
              style={blueButtonStyle}
              onMouseOver={e => e.currentTarget.style = {...blueButtonHoverStyle}}
              onMouseOut={e => e.currentTarget.style = {...blueButtonStyle}}
              onClick={() => history.push('/ai-workflows/ai-tour-planner')}
            >
              <FontAwesomeIcon icon={faArrowLeft} className="me-2" />
              Back to Tour Planner
            </Button>
          </div>
        </Container>
      </div>
      
      <Container className="mb-4">
        {error && <Alert variant="danger" className="dark-alert mb-4">{error}</Alert>}
        
        {loading ? (
          <div className="text-center my-5">
            <Spinner animation="border" variant="light" />
            <p className="text-light mt-3">Loading tour plans...</p>
          </div>
        ) : tourPlans.length === 0 ? (
          <Card className="dark-card p-4 text-center">
            <Card.Body>
              <p className="text-light">No saved tour plans found.</p>
              <Button 
                variant="primary"
                style={blueButtonStyle}
                onMouseOver={e => e.currentTarget.style = {...blueButtonHoverStyle}}
                onMouseOut={e => e.currentTarget.style = {...blueButtonStyle}}
                onClick={() => history.push('/ai-workflows/ai-tour-planner')}
                className="mt-3"
              >
                Create Your First Tour Plan
              </Button>
            </Card.Body>
          </Card>
        ) : (
          <Card className="dark-card">
            <Card.Header className="dark-card-header">
              <h5 className="mb-0">Your Saved Tour Plans</h5>
            </Card.Header>
            <Card.Body className="p-0">
              <Table responsive hover variant="dark" className="mb-0">
                <thead>
                  <tr>
                    <th>Tour Name</th>
                    <th>Artist</th>
                    <th>Created</th>
                    <th>Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {tourPlans.map(plan => (
                    <tr key={plan.id}>
                      <td>{plan.tour_name || 'Untitled Tour'}</td>
                      <td>{plan.artist_name || 'Unknown Artist'}</td>
                      <td>{formatDate(plan.created_at)}</td>
                      <td>
                        <Button 
                          variant="primary"
                          size="sm" 
                          className="me-2"
                          style={blueButtonStyle}
                          onMouseOver={e => e.currentTarget.style = {...blueButtonHoverStyle}}
                          onMouseOut={e => e.currentTarget.style = {...blueButtonStyle}}
                          onClick={() => handleViewPlan(plan)}
                        >
                          <FontAwesomeIcon icon={faEye} className="me-1" />
                          View
                        </Button>
                        <Button 
                          variant="outline-danger" 
                          size="sm"
                          onClick={() => handleShowDeleteModal(plan)}
                        >
                          <FontAwesomeIcon icon={faTrash} />
                        </Button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </Table>
            </Card.Body>
          </Card>
        )}
      </Container>
      
      {/* View Modal */}
      <Modal 
        show={viewModalOpen} 
        onHide={() => setViewModalOpen(false)}
        size="xl"
        centered
        contentClassName="dark-modal"
        dialogClassName="modal-90w"
      >
        <Modal.Header closeButton className="bg-dark text-white border-secondary">
          <Modal.Title>{selectedPlan?.tour_name || 'Tour Plan Details'}</Modal.Title>
          {/* <div className="ms-auto">
            <Button 
              variant="primary"
              size="sm" 
              onClick={handlePrintPlan}
              className="ms-2"
              style={blueButtonStyle}
              onMouseOver={e => e.currentTarget.style = {...blueButtonHoverStyle}}
              onMouseOut={e => e.currentTarget.style = {...blueButtonStyle}}
            >
              <FontAwesomeIcon icon={faPrint} className="me-2" />
              Print
            </Button>
          </div> */}
        </Modal.Header>
        <Modal.Body style={{ padding: 0, height: '80vh', backgroundColor: '#121212' }}>
          {loadingPlanContent ? (
            <div className="text-center my-3">
              <Spinner animation="border" variant="light" />
              <p className="text-light mt-3">Loading content...</p>
            </div>
          ) : (
            <iframe
              srcDoc={htmlContent}
              title={`Tour Plan - ${selectedPlan?.tour_name || 'Details'}`}
              style={{ 
                width: '100%', 
                height: '100%', 
                border: 'none',
                borderRadius: '0 0 8px 8px',
                backgroundColor: '#121212'
              }}
            />
          )}
        </Modal.Body>
      </Modal>
      
      {/* Delete Confirmation Modal */}
      <Modal 
        show={deleteModalOpen} 
        onHide={() => setDeleteModalOpen(false)}
        contentClassName="dark-modal"
      >
        <Modal.Header closeButton className="bg-dark text-white border-secondary">
          <Modal.Title>Confirm Delete</Modal.Title>
        </Modal.Header>
        <Modal.Body className="bg-dark text-white">
          Are you sure you want to delete the tour plan "{planToDelete?.tour_name || 'Untitled Tour'}"? 
          This action cannot be undone.
        </Modal.Body>
        <Modal.Footer className="bg-dark border-secondary">
          <Button 
            variant="primary"
            style={blueButtonStyle}
            onMouseOver={e => e.currentTarget.style = {...blueButtonHoverStyle}}
            onMouseOut={e => e.currentTarget.style = {...blueButtonStyle}}
            onClick={() => setDeleteModalOpen(false)}
          >
            Cancel
          </Button>
          <Button 
            variant="danger" 
            onClick={handleConfirmDelete}
          >
            Delete
          </Button>
        </Modal.Footer>
      </Modal>
    </Container>
  );
};

export default SavedTourPlans; 